import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  useMediaQuery,
} from '@mui/material'

import { useSidebarContext } from '../../../../contexts'

import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import StopIcon from '@mui/icons-material/Stop'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface SpeechToTextProps {
  setInputQuestion: (text: string) => void
}

// Extend the Window interface to include webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: any
    SpeechRecognition: any
  }
}

const SpeechToText = ({ setInputQuestion }: SpeechToTextProps) => {
  const [text, setText] = useState<string>('')
  const [isListening, setIsListening] = useState<boolean>(false)
  const [isSupported, setIsSupported] = useState<boolean>(false)
  const [interimText, setInterimText] = useState<string>('')

  const recognitionRef = useRef<any>(null)
  const isMobile = useMediaQuery('(max-width:600px)')
  const { openSpeechToText, setOpenSpeechToText } = useSidebarContext()

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (SpeechRecognition) {
      setIsSupported(true)
      recognitionRef.current = new SpeechRecognition()
      recognitionRef.current.continuous = true
      recognitionRef.current.interimResults = true
      recognitionRef.current.lang = 'en-US'

      recognitionRef.current.onresult = (event: any) => {
        let finalTranscript = ''
        let interimTranscript = ''

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            finalTranscript += String(transcript)
          } else {
            interimTranscript += String(transcript)
          }
        }

        setText((prev) => prev + finalTranscript)
        setInterimText(interimTranscript)
      }

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error)
        setIsListening(false)
      }

      recognitionRef.current.onend = () => {
        setIsListening(false)
        setInterimText('')
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [])

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start()
      setIsListening(true)
      setInterimText('')
    }
  }

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
      setInterimText('')
    }
  }

  const clearText = () => {
    setText('')
    setInterimText('')
  }

  const handleClickDone = () => {
    setInputQuestion(text)
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
      setInterimText('')
    }
    setOpenSpeechToText(false)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value)
  }

  if (!isSupported) {
    return (
      <Paper elevation={3} sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
        <Box textAlign='center'>
          <MicOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant='h6' gutterBottom>
            Speech Recognition Not Supported
          </Typography>
          <Typography color='text.secondary'>
            Your browser doesn`&apos;`t support speech recognition. Please try using Chrome or Edge.
          </Typography>
        </Box>
      </Paper>
    )
  }

  return (
    <Paper elevation={3} sx={{ mx: 'auto', p: 2, position: 'relative' }}>
      <IconButton
        onClick={() => {
          setOpenSpeechToText(false)
        }}
        size='small'
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          color: 'text.secondary',
        }}
      >
        <CloseIcon sx={{ color: 'red' }} />
      </IconButton>
      {/* <Typography
                variant="h5"
                fontWeight="bold"
                textAlign="center"
                mb={3}
            >
                Speech to Text
            </Typography> */}

      {/* Control Buttons */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        justifyContent='center'
        alignItems='center'
        mb={3}
        mt={3}
      >
        <Button
          sx={{ minWidth: isMobile ? '80%' : 'fit-content' }}
          variant='contained'
          color='primary'
          onClick={startListening}
          disabled={isListening}
          startIcon={<MicIcon />}
        >
          Start Listening
        </Button>

        <Button
          sx={{ minWidth: isMobile ? '80%' : 'fit-content' }}
          variant='contained'
          color='error'
          onClick={stopListening}
          disabled={!isListening}
          startIcon={<StopIcon />}
        >
          Stop Listening
        </Button>

        <Button
          sx={{ minWidth: isMobile ? '80%' : 'fit-content' }}
          variant='contained'
          color='inherit'
          onClick={clearText}
          startIcon={<DeleteIcon />}
        >
          Clear Text
        </Button>
      </Stack>

      {/* Status Indicator */}
      <Box display='flex' justifyContent='center' mb={2}>
        <Chip
          label={isListening ? 'Listening...' : 'Not Listening'}
          color={isListening ? 'success' : 'default'}
          variant='outlined'
          icon={
            <Box
              component='span'
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: isListening ? 'green' : 'grey.500',
              }}
            />
          }
        />
      </Box>

      {/* Text Area */}
      <Box position='relative'>
        <TextField
          multiline
          minRows={4}
          maxRows={11}
          fullWidth
          variant='outlined'
          value={text + interimText}
          onChange={handleTextChange}
          placeholder='Start speaking and your speech will appear here. You can also edit the text directly...'
        />

        {interimText && (
          <Typography
            variant='caption'
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              px: 1,
              py: 0.2,
              borderRadius: 1,
              fontSize: isMobile ? '0.6rem' : '0.75rem',
            }}
          >
            Processing...
          </Typography>
        )}
      </Box>

      <Stack direction='row' spacing={2} justifyContent='center' alignItems='center' my={2}>
        <Button
          disabled={!text}
          variant='contained'
          color='success'
          onClick={handleClickDone}
          startIcon={<DoneIcon />}
        >
          Done
        </Button>
      </Stack>

      {/* Instructions */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1-content'
          id='panel1-header'
        >
          <Typography component='span'> Instructions:</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant='subtitle1' color='text.secondary'>
            • Click &ldquo;Start Listening &rdquo; to begin speech recognition <br />
            • Speak clearly into your microphone <br />
            • Click &ldquo;Stop Listening &rdquo; to pause recognition <br />
            • Edit the text directly in the box for corrections <br />• Use &rdquo;Clear Text
            &ldquo; to remove all content
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Paper>
  )
}

export default SpeechToText
